/**
 * Compose AI: AI-powered Writing Tool — inline writing assistant
 *
 * Declares a single web-accessible-resource entry to every origin with no
 * `use_dynamic_url`, covering `assets/*` plus three named injection scripts
 * (`gdocsInject.js`, `gmailJsLoader.js`, `gmailInjectedLogic.js`). Everything under
 * `assets/`, including the packaged logo, is fetchable from any page at a fixed URL.
 *
 * The 128px logo is the cleanest signal: it loads through `probeImage` with no CORS
 * grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 2.1.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ddlbpiadoechcolndfeaonajmngmhblj';

export const composeAi: ExtensionDefinition = {
  id: ID,
  name: 'Compose AI: AI-powered Writing Tool',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 300_000,
  notes:
    'assets/* plus named injection scripts exposed to every origin with no dynamic URL. ' +
    'The compose logo loads without a CORS grant, so detection is conclusive.',
  signals: [
    {
      id: 'war-compose-logo-128',
      kind: 'web-accessible-resource',
      description:
        'Load assets/compose_logo_128.png via <img> — the vendor logo, exposed to every ' +
        'origin and readable without a CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'assets/compose_logo_128.png'),
    },
    {
      id: 'war-gdocs-inject-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch gdocsInject.js — a vendor-named Google Docs injection script exposed to ' +
        'every origin.',
      weight: 0.5,
      run: () => probeFetch(ID, 'gdocsInject.js'),
    },
    {
      id: 'war-gmail-js-loader',
      kind: 'web-accessible-resource',
      description: 'Fetch gmailJsLoader.js — the Gmail loader script, a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'gmailJsLoader.js'),
    },
  ],
};
